import { getElementRadius, getElectronegativity } from './elementRadii';
import { shouldCheckBond } from './bondCandidateFilter';
import { getBondPairCutoff } from './bondPairTable';

// ==================== Bond Calculation ====================

/**
 * Maximum plausible bond length (Angstrom). Any distance above this
 * is considered non-bonding regardless of covalent radii.
 */
const MAX_BOND_LENGTH = 3.2;

/**
 * Bond length scale: cutoff = min(predictedLength × BOND_SCALE, MAX_BOND_LENGTH)。
 * 1.3 而非 1.2，因为 Schomaker–Stevenson 用共价半径，对部分离子性氧化物（如
 * LiAlSiO4 的 Al–O 实测 ~2.1Å）偏紧；1.3 让 AlO₄/SiO₄ 四面体完整成键，且对
 * 金属-金属/同种阴离子仍由 shouldCheckBond 过滤（不会过连）。1.3↔1.4 饱和。
 */
export const BOND_SCALE = 1.3;

/**
 * 解析元素对的 cutoff（calculateBonds 与 buildDisplayBonds 共用，保证显示与真实
 * Bond 一致）。
 *
 * 优先级：Bond Pair Table > Schomaker–Stevenson。
 *   - 命中 Pair Table → cutoff = 该规则值，source = 'PairTable'。
 *   - 未命中 → cutoff = min(predictedLength · bondScale, MAX_BOND_LENGTH)，
 *     source = 'Schomaker'。
 *
 * @returns { cutoff, source } 供调用方判键与调试日志使用。
 */
function resolveBondCutoff(
  elementA: string,
  elementB: string,
  rA: number, rB: number,
  xA: number, xB: number,
  bondScale: number
): { cutoff: number; source: 'PairTable' | 'Schomaker' } {
  const pairCutoff = getBondPairCutoff(elementA, elementB);
  if (pairCutoff !== undefined) {
    return { cutoff: pairCutoff, source: 'PairTable' };
  }
  const predictedLength = rA + rB - 0.09 * Math.abs(xA - xB);
  return {
    cutoff: Math.min(predictedLength * bondScale, MAX_BOND_LENGTH),
    source: 'Schomaker',
  };
}

/**
 * Calculate chemical bonds between atoms.
 *
 * Uses the Schomaker-Stevenson equation to predict bond length:
 *   d(A-B) = r_A + r_B - c * |χ_A - χ_B|
 *
 * where c = 0.09 Å accounts for bond shortening due to ionic character.
 * A bond exists if the actual distance is within bondScale of the
 * predicted length AND above the minimum threshold (0.5 Å).
 *
 * Periodic boundary conditions: for each pair, checks the 27 nearest
 * periodic images of atom j to find the closest contact using the
 * minimum image convention.
 */
export function calculateBonds(
  atoms: Atom[],
  lattice: number[] | null,
  bondScale: number = BOND_SCALE
): Bond[] {
  if (atoms.length < 2) return [];

  const bonds: Bond[] = [];
  const radii = atoms.map(a => getElementRadius(a.element));
  const enegs = atoms.map(a => getElectronegativity(a.element));

  // [DEBUG] 开关：浏览器控制台执行 window.__DEBUG_BONDS = true
  const DBG = typeof window !== 'undefined' && (window as unknown as { __DEBUG_BONDS?: boolean }).__DEBUG_BONDS;

  // §1 打印每个 Atom 的真实数据：确认 Parser 是否正确保存 element（而非 label/species）。
  if (DBG) {
    // eslint-disable-next-line no-console
    console.log('[DEBUG calculateBonds] === atom dump (input) === atoms=' + atoms.length);
    atoms.forEach((a, i) => {
      const sp = (a as unknown as { species?: string }).species ?? '(none)';
      // eslint-disable-next-line no-console
      console.log(
        `[DEBUG calculateBonds] Atom ${i} element="${a.element}" label="${a.label ?? a.element}" species="${sp}" position=(${a.x.toFixed(3)},${a.y.toFixed(3)},${a.z.toFixed(3)})`
      );
    });
  }

  for (let i = 0; i < atoms.length; i++) {
    const r1 = radii[i];
    const x1 = enegs[i];
    for (let j = i + 1; j < atoms.length; j++) {
      // Bond Candidate Filter：先按元素对过滤，只有应当尝试成键的对才进入
      // Schomaker–Stevenson 距离判定（避免 Al-Li、Al-Si、O-O 等误判成键）。
      const spA = atoms[i].element;
      const spB = atoms[j].element;
      const labelA = atoms[i].label ?? spA;
      const labelB = atoms[j].label ?? spB;

      // §2/§3 打印 pair 与 shouldCheckBond（用 element，不是 label）。
      const allowed = shouldCheckBond(spA, spB);
      if (DBG) {
        // eslint-disable-next-line no-console
        console.log(
          `[DEBUG calculateBonds] Pair ${labelA}(element=${spA}, i=${i}) - ${labelB}(element=${spB}, j=${j}) | shouldCheckBond(${spA},${spB})=${allowed}${allowed ? '' : ' -> SKIPPED'}`
        );
      }
      if (!allowed) continue;

      const r2 = radii[j];
      const x2 = enegs[j];

      // §4 radius/电负性有效性检查：若 element 查不到（返回默认/NaN），判键必错。
      if (DBG) {
        const invalidA = r1 === undefined || Number.isNaN(r1) || r1 <= 0;
        const invalidB = r2 === undefined || Number.isNaN(r2) || r2 <= 0;
        // eslint-disable-next-line no-console
        console.log(
          `[DEBUG calculateBonds]   radius ${spA}=${r1} χ=${x1} | ${spB}=${r2} χ=${x2}${invalidA || invalidB ? ' -> INVALID RADIUS' : ''}`
        );
        if (invalidA || invalidB) continue;
      }

      // Cutoff 解析：Bond Pair Table 优先，未命中回退 Schomaker–Stevenson。
      // calculateBonds 与 buildDisplayBonds 共用 resolveBondCutoff，保证显示与真实 Bond 一致。
      const predictedLength = r1 + r2 - 0.09 * Math.abs(x1 - x2);
      const { cutoff, source } = resolveBondCutoff(spA, spB, r1, r2, x1, x2, bondScale);
      if (DBG) {
        // eslint-disable-next-line no-console
        console.log(
          `[DEBUG calculateBonds]   BondPair ${spA}-${spB} -> ${source}${source === 'PairTable' ? '' : ` (fallback, predicted=${predictedLength.toFixed(3)} scale=${bondScale})`} cutoff=${cutoff.toFixed(3)}`
        );
      }

      let bestDist = Infinity;
      let bestImage: [number, number, number] = [0, 0, 0];
      let bestTx = 0, bestTy = 0, bestTz = 0;

      if (lattice && lattice.length === 9) {
        // Check 27 periodic images (including origin) for minimum image distance
        for (let di = -1; di <= 1; di++) {
          for (let dj = -1; dj <= 1; dj++) {
            for (let dk = -1; dk <= 1; dk++) {
              const dx = atoms[j].x + di * lattice[0] + dj * lattice[3] + dk * lattice[6] - atoms[i].x;
              const dy = atoms[j].y + di * lattice[1] + dj * lattice[4] + dk * lattice[7] - atoms[i].y;
              const dz = atoms[j].z + di * lattice[2] + dj * lattice[5] + dk * lattice[8] - atoms[i].z;
              const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
              if (dist < bestDist) {
                bestDist = dist;
                bestImage = [
                  atoms[j].x + di * lattice[0] + dj * lattice[3] + dk * lattice[6],
                  atoms[j].y + di * lattice[1] + dj * lattice[4] + dk * lattice[7],
                  atoms[j].z + di * lattice[2] + dj * lattice[5] + dk * lattice[8],
                ];
                bestTx = di; bestTy = dj; bestTz = dk;
              }
            }
          }
        }
      } else {
        // No lattice: direct Euclidean distance
        const dx = atoms[j].x - atoms[i].x;
        const dy = atoms[j].y - atoms[i].y;
        const dz = atoms[j].z - atoms[i].z;
        bestDist = Math.sqrt(dx * dx + dy * dy + dz * dz);
        bestImage = [atoms[j].x, atoms[j].y, atoms[j].z];
        // bestTx/Ty/Tz 保持 (0,0,0)
      }

      const passCutoff = bestDist < cutoff;
      const passLower = bestDist > 0.5;
      const bonded = passCutoff && passLower;

      // §5/§6/§7/§8/§9 完整执行链：predicted/cutoff/距离/MIC 平移/PASS-FAIL 原因/Bond Created。
      if (DBG) {
        // eslint-disable-next-line no-console
        console.log(
          `[DEBUG calculateBonds]   Schomaker predictedLength=${predictedLength.toFixed(3)} bondScale=${bondScale} cutoff=${cutoff.toFixed(3)}`
        );
        // eslint-disable-next-line no-console
        console.log(
          `[DEBUG calculateBonds]   MIC bestDist=${bestDist.toFixed(3)} bestImage=(${bestImage[0].toFixed(3)},${bestImage[1].toFixed(3)},${bestImage[2].toFixed(3)}) translation=(${bestTx},${bestTy},${bestTz})`
        );
        // eslint-disable-next-line no-console
        console.log(
          `[DEBUG calculateBonds]   VERDICT distance=${bestDist.toFixed(3)} cutoff=${cutoff.toFixed(3)} PASS_cutoff=${passCutoff} PASS_lower(>0.5)=${passLower} => ${bonded ? 'Bond Created' : 'FAIL: ' + (!passCutoff ? 'distance >= cutoff' : 'distance <= 0.5')}`
        );
        if (bonded) {
          // eslint-disable-next-line no-console
          console.log(
            `[DEBUG calculateBonds]   >>> Bond Created ${labelA}(${spA}) -> ${labelB}(${spB}) distance=${bestDist.toFixed(3)}`
          );
        }
      }

      if (bonded) {
        bonds.push({
          from: i,
          to: j,
          fromPos: [atoms[i].x, atoms[i].y, atoms[i].z],
          toPos: bestImage,
        });
      }
    }
  }

  // [DEBUG] 打印 calculateBonds() 的全部结果，确认判键阶段是否已出现错误连键
  // （如 Al->Li）。开启方式：浏览器控制台执行 window.__DEBUG_BONDS = true。
  if (typeof window !== 'undefined' && (window as unknown as { __DEBUG_BONDS?: boolean }).__DEBUG_BONDS) {
    // eslint-disable-next-line no-console
    console.log('[DEBUG calculateBonds] atoms=' + atoms.length + ' bonds=' + bonds.length);
    bonds.forEach((b, k) => {
      const A = atoms[b.from], B = atoms[b.to];
      const ddx = b.toPos[0] - b.fromPos[0];
      const ddy = b.toPos[1] - b.fromPos[1];
      const ddz = b.toPos[2] - b.fromPos[2];
      const dist = Math.sqrt(ddx * ddx + ddy * ddy + ddz * ddz);
      const cutoff = Math.min(
        (getElementRadius(A?.element ?? '') + getElementRadius(B?.element ?? '')
          - 0.09 * Math.abs(getElectronegativity(A?.element ?? '') - getElectronegativity(B?.element ?? ''))) * 1.2,
        MAX_BOND_LENGTH
      );
      // eslint-disable-next-line no-console
      console.log(
        `[DEBUG calculateBonds] Bond #${k} from: index=${b.from} element=${A?.element}${A?.label ? '(' + A.label + ')' : ''} | to: index=${b.to} element=${B?.element}${B?.label ? '(' + B.label + ')' : ''} | distance=${dist.toFixed(3)} | cutoff=${cutoff.toFixed(3)}`
      );
    });
  }

  return bonds;
}

// ==================== Symmetry Expansion (fill the unit cell) ====================

/**
 * Parse one component of a symmetry operation (e.g. "-x+1/2", "x-y", "1/2")
 * into linear coefficients: value = cx*x + cy*y + cz*z + c.
 *
 * Crystallographic symmetry operations are always affine (linear in x,y,z
 * plus a constant), so this representation is exact.
 */
function parseOpComponent(expr: string): { cx: number; cy: number; cz: number; c: number } {
  const s = expr.replace(/\s+/g, '');
  let cx = 0, cy = 0, cz = 0, c = 0;

  // Split into signed terms: each term begins with an optional + or -.
  const terms = s.match(/[+-]?[^+-]+/g) ?? [];
  for (const term of terms) {
    if (!term) continue;
    const sign = term.startsWith('-') ? -1 : 1;
    const body = term.replace(/^[+-]/, '');

    if (body === 'x') cx += sign;
    else if (body === 'y') cy += sign;
    else if (body === 'z') cz += sign;
    else if (/^\d+\/\d+$/.test(body)) {
      const [num, den] = body.split('/').map(Number);
      c += sign * (num / den);
    } else if (/^\d+(?:\.\d+)?$/.test(body)) {
      c += sign * parseFloat(body);
    } else {
      // Coefficient*var form, e.g. "2x", "3z".
      const m = body.match(/^(\d+(?:\.\d+)?)([xyz])$/);
      if (m) {
        const v = parseFloat(m[1]);
        if (m[2] === 'x') cx += sign * v;
        else if (m[2] === 'y') cy += sign * v;
        else cz += sign * v;
      } else {
        // Unrecognized term — skip with a warning rather than crash the parse.
        // eslint-disable-next-line no-console
        console.warn(`[fillUnitCell] skipping unrecognized symmetry term "${term}"`);
      }
    }
  }
  return { cx, cy, cz, c };
}

/**
 * Parse a full operation string "fx, fy, fz" into a 3x3 rotation R and
 * translation t such that newFrac = R * frac + t.
 */
function parseSymmetryOp(op: string): { R: number[][]; t: number[] } {
  const parts = op.split(',').map(p => p.trim());
  const R: number[][] = [[0, 0, 0], [0, 0, 0], [0, 0, 0]];
  const t = [0, 0, 0];
  for (let i = 0; i < 3 && i < parts.length; i++) {
    const { cx, cy, cz, c } = parseOpComponent(parts[i]);
    R[i][0] = cx; R[i][1] = cy; R[i][2] = cz; t[i] = c;
  }
  return { R, t };
}

function isIdentityOp(op: { R: number[][]; t: number[] }): boolean {
  for (let i = 0; i < 3; i++) {
    for (let j = 0; j < 3; j++) {
      const expected = i === j ? 1 : 0;
      if (Math.abs(op.R[i][j] - expected) > 1e-9) return false;
    }
    if (Math.abs(op.t[i]) > 1e-9) return false;
  }
  return true;
}

/** Invert a 3x3 matrix; returns null if singular. */
function invertMatrix3(m: number[][]): number[][] | null {
  const a = m[0][0], b = m[0][1], c = m[0][2];
  const d = m[1][0], e = m[1][1], f = m[1][2];
  const g = m[2][0], h = m[2][1], i = m[2][2];
  const det = a * (e * i - f * h) - b * (d * i - f * g) + c * (d * h - e * g);
  if (Math.abs(det) < 1e-12) return null;
  const inv = 1 / det;
  return [
    [(e * i - f * h) * inv, (c * h - b * i) * inv, (b * f - c * e) * inv],
    [(f * g - d * i) * inv, (a * i - c * g) * inv, (c * d - a * f) * inv],
    [(d * h - e * g) * inv, (b * g - a * h) * inv, (a * e - b * d) * inv],
  ];
}

/**
 * Build the lattice matrix M (Cartesian = M * fractional) whose columns are
 * the a/b/c vectors, matching `fractionalToCartesian` above.
 */
function latticeMatrix(lattice: number[]): number[][] {
  return [
    [lattice[0], lattice[3], lattice[6]],
    [lattice[1], lattice[4], lattice[7]],
    [lattice[2], lattice[5], lattice[8]],
  ];
}

/** Wrap a fractional coordinate into [0, 1) with tolerance snapping. */
function wrapFractional(f: number): number {
  let r = f - Math.floor(f);
  if (r < 1e-6 || r > 1 - 1e-6) r = 0;
  return r;
}

/**
 * Expand the asymmetric unit into the full unit cell by applying all symmetry
 * operations. Atoms on special positions (lying on a mirror/rotation axis)
 * generate duplicate positions under some ops — those are deduplicated within
 * a fractional tolerance (1e-3, ~0.005 Å for a 5 Å cell).
 *
 * - No lattice, no symmetryOps, or only the identity op → returns the input
 *   unchanged (same reference), so already-complete cells (NaCl, P1 CIFs,
 *   POSCAR/XYZ) cost nothing.
 * - Otherwise returns a NEW structure with expanded atoms + recomputed bonds,
 *   lattice unchanged (the green UnitCellBox stays the same size).
 *   symmetryOps is cleared on the result so the function is idempotent.
 */
export function fillUnitCell(structure: ParsedStructure | null): ParsedStructure | null {
  if (!structure) return null;
  const { atoms, lattice, symmetryOps } = structure;
  if (!lattice || lattice.length !== 9 || !atoms.length || !symmetryOps || symmetryOps.length === 0) {
    return structure;
  }

  const ops = symmetryOps.map(parseSymmetryOp);
  if (ops.every(isIdentityOp)) return structure; // P1 / trivial — already complete

  const M = latticeMatrix(lattice);
  const Minv = invertMatrix3(M);
  if (!Minv) return structure; // degenerate lattice — leave as-is rather than crash

  const TOL = 1e-3;
  const expanded: Atom[] = [];

  const exists = (fx: number, fy: number, fz: number): boolean => {
    for (const a of expanded) {
      // Reconstruct fractional of already-added atoms via stored cartesian.
      const afx = Minv[0][0] * a.x + Minv[0][1] * a.y + Minv[0][2] * a.z;
      const afy = Minv[1][0] * a.x + Minv[1][1] * a.y + Minv[1][2] * a.z;
      const afz = Minv[2][0] * a.x + Minv[2][1] * a.y + Minv[2][2] * a.z;
      const dx = Math.abs(wrapFractional(afx) - fx);
      const dy = Math.abs(wrapFractional(afy) - fy);
      const dz = Math.abs(wrapFractional(afz) - fz);
      // Account for wrap-around at the 0/1 seam.
      if (Math.min(dx, 1 - dx) < TOL && Math.min(dy, 1 - dy) < TOL && Math.min(dz, 1 - dz) < TOL) {
        return true;
      }
    }
    return false;
  };

  for (const atom of atoms) {
    const fx0 = Minv[0][0] * atom.x + Minv[0][1] * atom.y + Minv[0][2] * atom.z;
    const fy0 = Minv[1][0] * atom.x + Minv[1][1] * atom.y + Minv[1][2] * atom.z;
    const fz0 = Minv[2][0] * atom.x + Minv[2][1] * atom.y + Minv[2][2] * atom.z;

    for (const op of ops) {
      const fx = wrapFractional(op.R[0][0] * fx0 + op.R[0][1] * fy0 + op.R[0][2] * fz0 + op.t[0]);
      const fy = wrapFractional(op.R[1][0] * fx0 + op.R[1][1] * fy0 + op.R[1][2] * fz0 + op.t[1]);
      const fz = wrapFractional(op.R[2][0] * fx0 + op.R[2][1] * fy0 + op.R[2][2] * fz0 + op.t[2]);
      if (exists(fx, fy, fz)) continue;

      // Back to Cartesian.
      const x = fx * lattice[0] + fy * lattice[3] + fz * lattice[6];
      const y = fx * lattice[1] + fy * lattice[4] + fz * lattice[7];
      const z = fx * lattice[2] + fy * lattice[5] + fz * lattice[8];
      expanded.push({ element: atom.element, label: atom.label, x, y, z });
    }
  }

  const bonds = calculateBonds(expanded, lattice, BOND_SCALE);
  return { ...structure, atoms: expanded, bonds, symmetryOps: undefined };
}

// ==================== Boundary Images ====================

/**
 * 边界周期像判定阈值（分数坐标）。原子分数坐标距某一晶胞面小于此值时，
 * 视为靠近该面，需补出其周期像。0.12 对典型晶胞（~5Å）约相当于一个共价半径
 * 量级：既厚到能覆盖跨越边界的键/原子，又薄到不会显示完整邻居晶胞。
 * 写成常量便于统一调整。
 */
const BOUNDARY_MARGIN = 0.12;

/**
 * 本函数用于构建显示用 Boundary Images（边界周期像）。
 *
 * 它不会改变晶胞大小（lattice 与绿色 UnitCellBox 完全不变）。
 * 不会生成真正 Supercell（不做 3×3×3 整体复制后再裁剪）。
 * 不会修改晶体学数据（原胞原子按引用保留，顺序与身份不变）。
 * 仅自动补充靠近晶胞边界、跨越六面/棱/角的周期像，使被切开的原子与化学键
 * 完整显示，视觉效果接近 VESTA 的边界显示。
 *
 * 实现（Minimal Boundary Images，而非「27 平移再裁剪」）：对每个原子先求分数
 * 坐标 (fx,fy,fz)，按其距各面的距离「按需」决定每个方向需要哪些周期平移——
 *   fx < margin       → tx 集合含 +1（靠近左面，像落在 fx+1）
 *   fx > 1 - margin   → tx 集合含 -1（靠近右面，像落在 fx-1）
 *   始终含 0（原原子本身）
 * y、z 同理。三方向集合做笛卡尔积，得到该原子真正需要的周期像集合：靠近一个
 * 面的原子补 1 个像、棱上补 3 个、角上补 7 个，而非固定 27 个。复制用既有平移
 * 公式 newPos = pos + tx*a + ty*b + tz*c。复制出的像带 isBoundaryImage = true，
 * originalIndex 指回源原胞原子（供显示层判键归一身份）、translation 记录周期平移，
 * 不再参与二次复制。displayAtoms = 原胞原子（按引用） + 像后，交 buildDisplayBonds
 * 判键。已完整位于晶胞内部、无任何方向靠近边界的结构不产生新原子，直接返回原引用。
 */
/**
 * 显示层判键：在 displayAtoms（原胞原子 + 边界周期像）上用直接欧氏距离判键。
 *
 * 去重策略：按「显示原子对」去重——每对 displayAtoms（含镜像）各画一条键，
 * 而非按 originalIndex 全局只留一条。这样镜像原子也能连键（VESTA 风格：可见
 * 键都画），避免镜像原子「悬空」。代价：同一物理键可能在原胞侧与镜像侧各画
 * 一次（视觉重叠，可接受）。同一晶体学原子的两个像之间（oa === ob）仍跳过，
 * 避免自键。i<j 双循环保证每对 displayAtoms 只访问一次，无需 Map 去重。
 *
 * 判键阈值与 calculateBonds 完全一致（Bond Pair Table 优先，回退 Schomaker-
 * Stevenson：cutoff = min((rA+rB - 0.09·|χA-χB|) · bondScale, MAX_BOND_LENGTH)，
 * 且 > 0.5Å）。不走 MIC（displayAtoms 已含边界像，直距即最短接触）；不调用
 * calculateBonds，故其默认 MIC 行为不变，侧栏计数等调用方不受影响。
 */
function buildDisplayBonds(displayAtoms: Atom[], bondScale: number = BOND_SCALE): Bond[] {
  const n = displayAtoms.length;
  if (n < 2) return [];

  const radii = displayAtoms.map(a => getElementRadius(a.element));
  const enegs = displayAtoms.map(a => getElectronegativity(a.element));

  // 身份：原胞原子无 originalIndex，用其 displayIndex（原胞原子按序在前 == 原胞下标）；
  // 镜像用 originalIndex（= 源原胞下标）。两者天然匹配。仅用于「自键」判定。
  const oid = (a: Atom, idx: number): number => a.originalIndex ?? idx;

  // 去重改为「按显示原子对」：每个显示原子对（含镜像）各画一条键，而非按
  // originalIndex 全局只留一条。这样镜像原子也能连键（VESTA 风格：可见键都画），
  // 代价是同一物理键可能在原胞侧与镜像侧各画一次（视觉重叠，可接受）。
  // i<j 双循环保证每对 displayAtoms 只访问一次，无需 Map 去重；自键（同一晶体学
  // 原子的两个像）仍跳过。
  const bonds: Bond[] = [];

  // [DEBUG] 开关：window.__DEBUG_BONDS = true
  const DBG = typeof window !== 'undefined' && (window as unknown as { __DEBUG_BONDS?: boolean }).__DEBUG_BONDS;

  for (let i = 0; i < n; i++) {
    const a = displayAtoms[i];
    const r1 = radii[i];
    const x1 = enegs[i];
    const oa = oid(a, i);
    for (let j = i + 1; j < n; j++) {
      const b = displayAtoms[j];
      // Bond Candidate Filter：与 calculateBonds 一致，先按元素对过滤，只有应当
      // 尝试成键的对才进入距离/Schomaker–Stevenson 判定（避免 Al-Li、O-O 等误判）。
      // 镜像与原胞原子同 element，故镜像之间的过滤等价于原胞对过滤。
      if (!shouldCheckBond(a.element, b.element)) continue;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const dz = b.z - a.z;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (dist <= 0.5) continue;

      // Cutoff 解析：与 calculateBonds 完全一致（Bond Pair Table 优先，回退 Schomaker）。
      const r2 = radii[j];
      const x2 = enegs[j];
      const predictedLength = r1 + r2 - 0.09 * Math.abs(x1 - x2);
      const { cutoff, source } = resolveBondCutoff(a.element, b.element, r1, r2, x1, x2, bondScale);
      if (DBG) {
        // eslint-disable-next-line no-console
        console.log(
          `[DEBUG buildDisplayBonds]   BondPair ${a.element}-${b.element} -> ${source}${source === 'PairTable' ? '' : ` (fallback, predicted=${predictedLength.toFixed(3)})`} cutoff=${cutoff.toFixed(3)}`
        );
      }

      const ob = oid(b, j);

      // [DEBUG] 候选日志：通过 cutoff 的所有候选都打印（含错误的 Al-Li）。
      if (DBG && dist < cutoff) {
        // eslint-disable-next-line no-console
        console.log(
          `[DEBUG buildDisplayBonds] Candidate ${a.element}${a.label ? '(' + a.label + ')' : ''}(display=${i} original=${oa} tr=[${a.translation ? a.translation.join(',') : '0,0,0'}]) -> ${b.element}${b.label ? '(' + b.label + ')' : ''}(display=${j} original=${ob} tr=[${b.translation ? b.translation.join(',') : '0,0,0'}]) distance=${dist.toFixed(3)} cutoff=${cutoff.toFixed(3)}${oa === ob ? ' [SELF-SKIP]' : ''}`
        );
      }

      if (dist >= cutoff) continue;

      // 同一晶体学原子的两个像之间不建键（避免自键，如 O8 与 O8(+1,0,0)）。
      if (oa === ob) continue;

      // 每对显示原子（含镜像）各画一条键，不再按 originalIndex 全局去重。
      if (DBG) {
        // eslint-disable-next-line no-console
        console.log(
          `[DEBUG buildDisplayBonds] SAVE ${a.element}(display=${i} orig=${oa}) -> ${b.element}(display=${j} orig=${ob}) distance=${dist.toFixed(3)} NEW`
        );
      }
      bonds.push({
        from: i,
        to: j,
        fromPos: [a.x, a.y, a.z],
        toPos: [b.x, b.y, b.z],
      });
    }
  }

  const result = bonds;

  // [DEBUG] 配位汇总：每个显示原子（按 displayIndex）连了哪些 element/displayIndex。
  if (DBG) {
    const coord = new Map<number, { element: string; neighbors: string[] }>();
    for (const b of result) {
      const A = displayAtoms[b.from], B = displayAtoms[b.to];
      const ca = coord.get(b.from) ?? { element: A.element, neighbors: [] };
      ca.neighbors.push(`${B.element}(disp=${b.to})`);
      coord.set(b.from, ca);
      const cb = coord.get(b.to) ?? { element: B.element, neighbors: [] };
      cb.neighbors.push(`${A.element}(disp=${b.from})`);
      coord.set(b.to, cb);
    }
    // eslint-disable-next-line no-console
    console.log('[DEBUG buildDisplayBonds] === coordination summary ===');
    for (const [di, c] of coord) {
      // eslint-disable-next-line no-console
      console.log(`[DEBUG buildDisplayBonds] ${c.element}(display=${di}) -> ${c.neighbors.join(', ')}`);
    }
  }

  return result;
}

export function buildBoundaryImages(structure: ParsedStructure): ParsedStructure {
  const { atoms, lattice } = structure;

  // Step 1：无 lattice / 长度不对 / 无原子 → 直接返回原结构。
  if (!lattice || lattice.length !== 9 || atoms.length === 0) {
    return structure;
  }

  // 复用既有 latticeMatrix / invertMatrix3，不重新实现求逆与平移。
  const Minv = invertMatrix3(latticeMatrix(lattice));
  if (!Minv) return structure; // 退化晶格：求逆失败，保持原样。

  // lattice 列向量 a/b/c（与 UnitCellBox 完全一致）。
  const ax = lattice[0], ay = lattice[1], az = lattice[2];
  const bx = lattice[3], by = lattice[4], bz = lattice[5];
  const cx = lattice[6], cy = lattice[7], cz = lattice[8];

  const margin = BOUNDARY_MARGIN;
  const seen = new Set<string>();
  const images: Atom[] = [];

  for (let i = 0; i < atoms.length; i++) {
    const atom = atoms[i];

    // Step 2：分数坐标（笛卡尔 → 分数，用 Minv）。后续判定全部在分数空间完成。
    const fx = Minv[0][0] * atom.x + Minv[0][1] * atom.y + Minv[0][2] * atom.z;
    const fy = Minv[1][0] * atom.x + Minv[1][1] * atom.y + Minv[1][2] * atom.z;
    const fz = Minv[2][0] * atom.x + Minv[2][1] * atom.y + Minv[2][2] * atom.z;

    // Step 3：按距各面距离，按需决定每个方向的周期平移集合（始终含 0）。
    const txs: number[] = [0];
    if (fx < margin) txs.push(1);       // 靠近左面 → 像落在 fx+1
    if (fx > 1 - margin) txs.push(-1);  // 靠近右面 → 像落在 fx-1
    const tys: number[] = [0];
    if (fy < margin) tys.push(1);
    if (fy > 1 - margin) tys.push(-1);
    const tzs: number[] = [0];
    if (fz < margin) tzs.push(1);
    if (fz > 1 - margin) tzs.push(-1);

    // Step 4：三方向笛卡尔积 = 该原子真正需要的周期像集合（非固定 27）。
    for (const tx of txs) {
      for (const ty of tys) {
        for (const tz of tzs) {
          if (tx === 0 && ty === 0 && tz === 0) continue; // (0,0,0) 是原原子，按引用保留

          // Step 7：去重——同一原子的同一 (tx,ty,tz) 仅生成一次。
          const key = `${i}|${tx},${ty},${tz}`;
          if (seen.has(key)) continue;
          seen.add(key);

          // Step 5：复用既有平移公式 newPos = pos + tx*a + ty*b + tz*c。
          // Step 6：像保留 element/label 等全部属性，并标记 isBoundaryImage = true。
          // originalIndex 指回源原胞原子下标（= 其 displayIndex），使显示层能把
          // 同一晶体学原子的多个周期像归一为同一身份；translation 记录周期平移。
          images.push({
            element: atom.element,
            label: atom.label,
            x: atom.x + tx * ax + ty * bx + tz * cx,
            y: atom.y + tx * ay + ty * by + tz * cy,
            z: atom.z + tx * az + ty * bz + tz * cz,
            isBoundaryImage: true,
            originalIndex: i,
            translation: [tx, ty, tz],
          });
        }
      }
    }
  }

  // 已完整位于晶胞内部的结构：无新增 → 直接返回原引用，避免无意义重建。
  if (images.length === 0) return structure;

  // Step 9：atoms = 原胞原子（按引用） + Boundary Images；lattice 保持原始。
  // 原原子按引用保留（isBoundaryImage 未设即视为 false），保证身份不变，
  // 也避免破坏 CrystalViewer 中 selectedAtom === atom 的引用比较。
  const displayAtoms = atoms.concat(images);

  // [DEBUG] 打印全部 displayAtoms，确认 Boundary Image 的 originalIndex/translation 是否正确。
  if (typeof window !== 'undefined' && (window as unknown as { __DEBUG_BONDS?: boolean }).__DEBUG_BONDS) {
    // eslint-disable-next-line no-console
    console.log('[DEBUG buildBoundaryImages] unitCell=' + atoms.length + ' images=' + images.length + ' displayAtoms=' + displayAtoms.length);
    displayAtoms.forEach((a, idx) => {
      // eslint-disable-next-line no-console
      console.log(
        `[DEBUG buildBoundaryImages] displayIndex=${idx} originalIndex=${a.originalIndex ?? idx} element=${a.element}${a.label ? '(' + a.label + ')' : ''} translation=[${a.translation ? a.translation.join(',') : '0,0,0'}] image=${a.isBoundaryImage === true} position=(${a.x.toFixed(3)},${a.y.toFixed(3)},${a.z.toFixed(3)})`
      );
    });
  }

  // Step 8：在显示原子集合上「直接距离判键」生成显示用 Bond。
  // displayAtoms 已含边界周期像，真正相邻的像之间直距即为最短接触，无需 MIC
  // 折叠（MIC 平局会把键折到不存在的像上 → 悬空 → 角原子失配位）。直距判键使
  // toPos 恒为显示原子，永无悬空；并以无序端点去重，每条线段只画一次。
  // 不调用 calculateBonds，其默认 MIC 行为不变，侧栏计数等调用方不受影响。
  const displayBonds = buildDisplayBonds(displayAtoms, BOND_SCALE);

  // [DEBUG] 打印最终保存的 Display Bond（from/to display index + species + originalIndex + distance）。
  if (typeof window !== 'undefined' && (window as unknown as { __DEBUG_BONDS?: boolean }).__DEBUG_BONDS) {
    // eslint-disable-next-line no-console
    console.log('[DEBUG buildBoundaryImages] saved displayBonds=' + displayBonds.length);
    displayBonds.forEach((b, k) => {
      const A = displayAtoms[b.from], B = displayAtoms[b.to];
      const dist = Math.hypot(b.toPos[0] - b.fromPos[0], b.toPos[1] - b.fromPos[1], b.toPos[2] - b.fromPos[2]);
      // eslint-disable-next-line no-console
      console.log(
        `[DEBUG buildBoundaryImages] SAVE #${k} ${A?.element}${A?.label ? '(' + A.label + ')' : ''}(display=${b.from} original=${A?.originalIndex ?? b.from}) -> ${B?.element}${B?.label ? '(' + B.label + ')' : ''}(display=${b.to} original=${B?.originalIndex ?? b.to}) distance=${dist.toFixed(3)}`
      );
    });
  }

  return { ...structure, atoms: displayAtoms, bonds: displayBonds };
}

// ==================== Data Types ====================

/**
 * 从原子标签（CIF label 或 type_symbol）提取纯元素符号。
 *   "O8" → "O"、"Al12" → "Al"、"Li2" → "Li"、"O2-" → "O"。
 * 仅在 Parser 内部使用；解析后整个程序不再解析字符串，化学计算一律用 Atom.element。
 */
const ELEMENT_RE = /^([A-Z][a-z]?)/;
export function elementFromLabel(s: string): string {
  const m = s.match(ELEMENT_RE);
  return m ? m[1] : s;
}

export interface Atom {
  /** 纯元素符号，如 "O"/"Al"/"Li"。所有化学计算（半径/电负性/判键）一律用此。 */
  element: string;
  /**
   * 原子标签，如 CIF 的 "O8"/"Al1"。无标签格式（POSCAR）省略；
   * XYZ 中 label == element。仅用于显示，绝不参与化学计算。
   */
  label?: string;
  x: number;
  y: number;
  z: number;
  /**
   * 周期像标记。原胞原子不设置此字段（视为 false）；由 buildBoundaryImages
   * 补出的跨边界周期像为 true。仅用于显示区分，不影响晶体学意义。
   */
  isBoundaryImage?: boolean;
  /**
   * 晶体学身份：该像对应的原胞原子下标。原胞原子不设置（buildDisplayBonds 用其
   * 在 displayAtoms 中的下标推导，因原胞原子按序在前，下标即原胞下标）；镜像设为
   * 源原胞原子下标。供显示层把同一晶体学原子的多个周期像归一为同一身份。
   */
  originalIndex?: number;
  /**
   * 周期平移 [tx,ty,tz]。原胞原子为 [0,0,0]（未设置即视为 [0,0,0]）；镜像为相对
   * 原胞的整数平移。仅显示用，不影响晶体学意义。
   */
  translation?: [number, number, number];
}

/**
 * 边界归一：把后端返回的旧形状 {species,x,y,z} 归一为新形状 {element,label,x,y,z}。
 * 后端 server/projects/structure.js 用 pymatgen 返回 {species: str(site.specie),...}，
 * 其 species 本就是纯元素符号，故 element=species、label=species。
 * 已是新形状（含 element）的原子透传。保留 isBoundaryImage/originalIndex/translation。
 */
export function normalizeAtoms(raw: Atom[]): Atom[] {
  return raw.map(a => {
    const element = a.element
      ?? (a as unknown as { species?: string }).species
      ?? 'X';
    const label = a.label
      ?? (a as unknown as { species?: string }).species
      ?? element;
    return {
      element,
      label,
      x: a.x,
      y: a.y,
      z: a.z,
      ...(a.isBoundaryImage !== undefined ? { isBoundaryImage: a.isBoundaryImage } : {}),
      ...(a.originalIndex !== undefined ? { originalIndex: a.originalIndex } : {}),
      ...(a.translation !== undefined ? { translation: a.translation } : {}),
    };
  });
}

export interface Bond {
  from: number;
  to: number;
  fromPos: [number, number, number];
  toPos: [number, number, number];
}

export interface ParsedStructure {
  atoms: Atom[];
  lattice: number[] | null;
  formula?: string;
  comment?: string;
  bonds?: Bond[];
  /**
   * Raw crystallographic symmetry operations in xyz notation
   * (e.g. "x, y, z", "-x, -y, -z", "x+1/2, y+1/2, z"). Only CIF files
   * carry these. When present and non-trivial, `fillUnitCell` expands the
   * asymmetric-unit atom list into the full unit cell. POSCAR/XYZ never
   * set this (no symmetry concept), so `fillUnitCell` is a passthrough.
   */
  symmetryOps?: string[];
}

/**
 * Build 3x3 lattice matrix from cell parameters (a, b, c, alpha, beta, gamma).
 * Convention: a along x, b in xy-plane, c completes right-handed system.
 */
function latticeFromParams(
  a: number, b: number, c: number,
  alpha: number, beta: number, gamma: number
): number[] {
  const alphaRad = (alpha * Math.PI) / 180;
  const betaRad = (beta * Math.PI) / 180;
  const gammaRad = (gamma * Math.PI) / 180;

  const ax = a;
  const bx = b * Math.cos(gammaRad);
  const by = b * Math.sin(gammaRad);
  const cx = c * Math.cos(betaRad);
  const cy = c * (Math.cos(alphaRad) - Math.cos(betaRad) * Math.cos(gammaRad)) / Math.sin(gammaRad);
  const cz = Math.sqrt(c * c - cx * cx - cy * cy);

  return [ax, 0, 0, bx, by, 0, cx, cy, cz];
}

/**
 * Convert fractional coordinates to Cartesian using lattice matrix.
 */
function fractionalToCartesian(frac: [number, number, number], lattice: number[]): [number, number, number] {
  return [
    frac[0] * lattice[0] + frac[1] * lattice[3] + frac[2] * lattice[6],
    frac[0] * lattice[1] + frac[1] * lattice[4] + frac[2] * lattice[7],
    frac[0] * lattice[2] + frac[1] * lattice[5] + frac[2] * lattice[8],
  ];
}

// ==================== CIF Parser ====================

/**
 * Extract symmetry operations from a CIF's `_symmetry_equiv_pos_as_xyz` loop.
 * Returns raw operation strings like ["x, y, z", "-x, -y, -z", ...].
 * Handles both the loop_ form and the inline single-value form (common for P1).
 */
function parseSymmetryOps(content: string): string[] {
  const ops: string[] = [];

  // loop_ form: headers _symmetry_equiv_pos_* followed by data rows.
  const loopRe = /loop_\s*\n((?:\s*_symmetry_equiv_pos_\S+\s*\n)+)((?:\s*\S[\s\S]*?))(?:^\s*$|\n\s*_|\n\s*loop_|\n\s*data_)/im;
  const m = content.match(loopRe);
  if (m) {
    const headers = m[1].trim().split(/\s*\n\s*/).map(h => h.trim());
    const xyzIdx = headers.indexOf('_symmetry_equiv_pos_as_xyz');
    if (xyzIdx >= 0) {
      for (const row of m[2].split('\n')) {
        if (!row.trim()) continue;
        const vals = row.trim().match(/'[^']*'|"[^"]*"|\S+/g);
        if (!vals || !vals[xyzIdx]) continue;
        ops.push(vals[xyzIdx].replace(/^['"]|['"]$/g, '').trim());
      }
    }
  }

  // Fallback: inline single-value form `_symmetry_equiv_pos_as_xyz 'x, y, z'`.
  if (ops.length === 0) {
    const inline = content.match(/_symmetry_equiv_pos_as_xyz\s+['"]([^'"]+)['"]/);
    if (inline) ops.push(inline[1].trim());
  }

  return ops;
}

export function parseCIF(content: string): ParsedStructure {
  const atoms: Atom[] = [];
  let a = 1, b = 1, c = 1, alpha = 90, beta = 90, gamma = 90;

  // Extract cell parameters
  const cellAMatch = content.match(/_cell_length_a\s+([\d.]+)/i);
  const cellBMatch = content.match(/_cell_length_b\s+([\d.]+)/i);
  const cellCMatch = content.match(/_cell_length_c\s+([\d.]+)/i);
  const alphaMatch = content.match(/_cell_angle_alpha\s+([\d.]+)/i);
  const betaMatch = content.match(/_cell_angle_beta\s+([\d.]+)/i);
  const gammaMatch = content.match(/_cell_angle_gamma\s+([\d.]+)/i);

  if (cellAMatch) a = parseFloat(cellAMatch[1]);
  if (cellBMatch) b = parseFloat(cellBMatch[1]);
  if (cellCMatch) c = parseFloat(cellCMatch[1]);
  if (alphaMatch) alpha = parseFloat(alphaMatch[1]);
  if (betaMatch) beta = parseFloat(betaMatch[1]);
  if (gammaMatch) gamma = parseFloat(gammaMatch[1]);

  const lattice = latticeFromParams(a, b, c, alpha, beta, gamma);

  // Extract formula
  const formulaMatch = content.match(/_chemical_formula_sum\s+['"]?(.+?)['"]?\s*$/im);
  const formula = formulaMatch ? formulaMatch[1].trim() : undefined;

  // Parse atom site loop - find the loop_ section with _atom_site_ data.
  // Data lines = non-empty lines that don't start a new section (_, loop_, data_).
  // Each line captured with optional trailing \n so the final line works without a trailing newline.
  const loopMatch = content.match(/loop_\s*\n((?:\s*_atom_site_\S+\s*\n)+)((?:(?!\s*(?:_|loop_|data_))\s*\S[^\n]*\n?)*)/i);
  if (!loopMatch) {
    // Try alternative: parse _atom_site_ lines outside loop
    parseAtomSiteLines(content, lattice, atoms);
  } else {
    const headerBlock = loopMatch[1];
    const dataBlock = loopMatch[2];

    // Determine column indices
    const headers = headerBlock.trim().split(/\s*\n\s*/).map(h => h.trim().replace(/_atom_site_/, ''));
    const typeIdx = headers.indexOf('type_symbol');
    const labelIdx = headers.indexOf('label');
    const fractXIdx = headers.indexOf('fract_x');
    const fractYIdx = headers.indexOf('fract_y');
    const fractZIdx = headers.indexOf('fract_z');

    if (fractXIdx < 0 || fractYIdx < 0 || fractZIdx < 0) {
      // No fractional coords found, skip
    } else {
      const dataLines = dataBlock.trim().split('\n').filter(line => line.trim());

      for (const line of dataLines) {
        const parts = line.trim().split(/\s+/);
        if (parts.length < Math.max(fractXIdx, fractYIdx, fractZIdx) + 1) continue;

        // element：优先用 type_symbol（本就是纯元素符号，但防御 "O2-" 等带电形式也提取）；
        // 否则从 label 提取纯元素符号。label：始终保留 CIF 原始标签（如 "O8"/"Al1"）。
        let element: string | undefined;
        let label: string | undefined;
        if (typeIdx >= 0 && parts[typeIdx]) {
          element = elementFromLabel(parts[typeIdx]);
        }
        if (labelIdx >= 0 && parts[labelIdx]) {
          label = parts[labelIdx];
          if (!element) element = elementFromLabel(label);
        }
        if (!element) continue;

        const fx = parseFloat(parts[fractXIdx]);
        const fy = parseFloat(parts[fractYIdx]);
        const fz = parseFloat(parts[fractZIdx]);
        if (isNaN(fx) || isNaN(fy) || isNaN(fz)) continue;

        const [x, y, z] = fractionalToCartesian([fx, fy, fz], lattice);
        atoms.push({ element, label, x, y, z });
      }
    }
  }

  const symmetryOps = parseSymmetryOps(content);
  return { atoms, lattice, formula, symmetryOps };
}

function parseAtomSiteLines(content: string, lattice: number[], atoms: Atom[]) {
  const regex = /_atom_site_type_symbol\s+(\w+)\s*\n\s*_atom_site_fract_x\s+([\d.-]+)\s*\n\s*_atom_site_fract_y\s+([\d.-]+)\s*\n\s*_atom_site_fract_z\s+([\d.-]+)/g;
  let match;
  while ((match = regex.exec(content)) !== null) {
    const symbol = match[1];
    const fx = parseFloat(match[2]);
    const fy = parseFloat(match[3]);
    const fz = parseFloat(match[4]);
    const [x, y, z] = fractionalToCartesian([fx, fy, fz], lattice);
    // type_symbol 作 element（防御性提取纯符号）；无独立 label 列，label = symbol。
    atoms.push({ element: elementFromLabel(symbol), label: symbol, x, y, z });
  }
}

// ==================== POSCAR Parser ====================

export function parsePOSCAR(content: string): ParsedStructure {
  const lines = content.trim().split('\n').map(l => l.trim()).filter(l => l);
  let idx = 0;

  const comment = lines[idx++];

  // Scaling factor
  const scale = parseFloat(lines[idx++]);

  // Lattice vectors
  const latVecs: number[] = [];
  for (let i = 0; i < 3; i++) {
    const parts = lines[idx++].split(/\s+/).map(Number);
    latVecs.push(parts[0] * scale, parts[1] * scale, parts[2] * scale);
  }

  // Element symbols (line may be element names or atom counts)
  const line6 = lines[idx].trim();
  const countsLine = /^\d/.test(line6) ? line6 : lines[++idx].trim();
  const symbolsLine = /^\d/.test(line6) ? lines[++idx]?.trim() : line6;

  const counts = countsLine.split(/\s+/).map(Number);
  const symbols = symbolsLine ? symbolsLine.split(/\s+/) : [];

  // Skip selective dynamics line if present
  idx++;
  const coordType = lines[idx]?.trim().toLowerCase();
  if (coordType === 'selective' || coordType === 's') {
    idx++; // skip selective dynamics
    idx++; // skip coordinate type line
  } else {
    idx++; // skip coordinate type line
  }

  // Read atom coordinates
  const atoms: Atom[] = [];
  const isDirect = coordType?.startsWith('d') || coordType?.startsWith('D');
  let atomIdx = 0;
  for (let s = 0; s < counts.length; s++) {
    for (let i = 0; i < counts[s]; i++) {
      const parts = lines[idx++]?.split(/\s+/).map(Number);
      if (!parts || parts.length < 3) break;
      const element = elementFromLabel(symbols[s] || 'X');

      if (isDirect) {
        const [x, y, z] = fractionalToCartesian([parts[0], parts[1], parts[2]], latVecs);
        atoms.push({ element, x, y, z });
      } else {
        atoms.push({ element, x: parts[0], y: parts[1], z: parts[2] });
      }
      atomIdx++;
    }
  }

  return { atoms, lattice: latVecs, comment, formula: symbols.join('') };
}

// ==================== XYZ / extxyz Parser ====================

export function parseExtxyz(content: string): ParsedStructure {
  const lines = content.trim().split('\n');
  if (lines.length < 2) return { atoms: [], lattice: null };

  const numAtoms = parseInt(lines[0], 10);
  if (isNaN(numAtoms)) throw new Error('第一行不是原子数');

  const secondLine = lines[1];
  let lattice: number[] | null = null;

  // Extract Lattice
  const latticeMatch = secondLine.match(/Lattice="([^"]+)"/);
  if (latticeMatch) {
    lattice = latticeMatch[1].split(/[,\s]+/).map(Number);
  }

  // Parse atom lines
  const atomLines = lines.slice(2, 2 + numAtoms);
  const atoms: Atom[] = atomLines.map(line => {
    const trimmed = line.trim();
    let parts = trimmed.split(/\s+/);
    if (parts.length < 4) {
      parts = trimmed.split(',').map(p => p.trim());
    }
    if (parts.length >= 4) {
      // XYZ：element 与 label 同为原子行首符号（spec：XYZ → element="O", label="O"）。
      const symbol = parts[0];
      return {
        element: elementFromLabel(symbol),
        label: symbol,
        x: parseFloat(parts[1]),
        y: parseFloat(parts[2]),
        z: parseFloat(parts[3]),
      };
    }
    return null;
  }).filter(Boolean) as Atom[];

  return { atoms, lattice };
}

/**
 * Parse multi-frame XYZ / extxyz content.
 *
 * Standard multi-frame XYZ format:
 *   N
 *   comment line (optional Lattice="..." for extxyz)
 *   atom1 x y z
 *   ...
 *   N
 *   comment line
 *   ...
 *
 * Returns an array of ParsedStructure, one per frame.
 * For single-frame files, returns a single-element array.
 */
export function parseMultiFrameXYZ(content: string): ParsedStructure[] {
  const rawLines = content.trim().split('\n');
  const frames: ParsedStructure[] = [];
  let idx = 0;

  while (idx < rawLines.length) {
    // Skip empty lines between frames
    while (idx < rawLines.length && rawLines[idx].trim() === '') {
      idx++;
    }
    if (idx >= rawLines.length) break;

    const numAtoms = parseInt(rawLines[idx].trim(), 10);
    if (isNaN(numAtoms) || numAtoms <= 0) {
      idx++;
      continue;
    }

    idx++; // move past atom count line

    if (idx >= rawLines.length) break;
    const commentLine = rawLines[idx].trim();
    idx++; // move past comment line

    let lattice: number[] | null = null;
    const latticeMatch = commentLine.match(/Lattice="([^"]+)"/);
    if (latticeMatch) {
      lattice = latticeMatch[1].split(/[,\s]+/).map(Number);
    }

    const atoms: Atom[] = [];
    const endLine = Math.min(idx + numAtoms, rawLines.length);
    for (; idx < endLine; idx++) {
      const line = rawLines[idx].trim();
      if (!line) {
        // Empty line within atom block — skip
        continue;
      }
      let parts = line.split(/\s+/);
      if (parts.length < 4) {
        parts = line.split(',').map(p => p.trim());
      }
      if (parts.length >= 4) {
        const symbol = parts[0];
        atoms.push({
          element: elementFromLabel(symbol),
          label: symbol,
          x: parseFloat(parts[1]),
          y: parseFloat(parts[2]),
          z: parseFloat(parts[3]),
        });
      }
    }

    if (atoms.length > 0) {
      frames.push({
        atoms,
        lattice,
        comment: commentLine || undefined,
      });
    }
  }

  return frames;
}

// ==================== Auto-detect Parser ====================

export function autoParse(content: string, filename?: string): ParsedStructure {
  const trimmed = content.trim();
  const ext = filename?.split('.').pop()?.toLowerCase();

  if (ext === 'cif') return parseCIF(trimmed);
  if (ext === 'poscar' || ext === 'vasp' || filename?.toUpperCase() === 'POSCAR') return parsePOSCAR(trimmed);
  if (ext === 'xyz' || ext === 'extxyz') return parseExtxyz(trimmed);

  // Auto-detect by content
  if (/^data_/i.test(trimmed) || /_cell_length/i.test(trimmed) || /_audit_creation/i.test(trimmed)) {
    return parseCIF(trimmed);
  }
  if (/^\s*\d+\s*\n/.test(trimmed) && /Lattice=/.test(trimmed)) {
    return parseExtxyz(trimmed);
  }
  // POSCAR: first line is comment, second is a number, then 3 lattice vectors
  const lines = trimmed.split('\n').map(l => l.trim()).filter(l => l);
  if (lines.length >= 5 && !isNaN(parseFloat(lines[1])) && !isNaN(parseFloat(lines[2].split(/\s+/)[0]))) {
    return parsePOSCAR(trimmed);
  }

  throw new Error('无法识别的文件格式');
}

// ==================== Example Structures ====================

export function getNaClStructure(): ParsedStructure {
  const a = 5.64;
  const lattice = [a, 0, 0, 0, a, 0, 0, 0, a];
  const atoms: Atom[] = [];
  // Na at corners and face centers
  const naPositions = [
    [0, 0, 0], [0.5, 0.5, 0], [0.5, 0, 0.5], [0, 0.5, 0.5],
  ];
  // Cl at edge centers and body center
  const clPositions = [
    [0.5, 0, 0], [0, 0.5, 0], [0, 0, 0.5], [0.5, 0.5, 0.5],
  ];
  for (const pos of naPositions) {
    atoms.push({ element: 'Na', x: pos[0] * a, y: pos[1] * a, z: pos[2] * a });
  }
  for (const pos of clPositions) {
    atoms.push({ element: 'Cl', x: pos[0] * a, y: pos[1] * a, z: pos[2] * a });
  }
  return { atoms, lattice, formula: 'NaCl' };
}
